import { Link, useLocation, useNavigate } from "react-router-dom";
import { FileText, Menu, X, LogOut, Settings, ChevronDown, Plus, Upload, List, Package, Users, FileSpreadsheet, Wallet, BarChart3, Inbox, TrendingUp, Shield, Clock, LayoutDashboard, ScrollText } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { usePlan } from "../context/PlanContext";
import { NotificationBell } from "./NotificationBell";
import LanguageSwitcher from "./LanguageSwitcher";
import { getLangFromPath, prefixPath, useT } from "../seo/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

function NavDropdown({ label, items, isActive }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const anyActive = items.some((i) => i.active);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`nav-dropdown-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          anyActive
            ? "bg-[#EFF6FF] text-[#2563EB]"
            : "text-[#4B5563] hover:text-[#111827] hover:bg-[#F3F4F6]"
        }`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-1 w-52 bg-white rounded-xl border border-[#E5E7EB] shadow-lg py-1 z-50"
          >
            {items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                data-testid={`nav-link-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${
                  item.active
                    ? "bg-[#EFF6FF] text-[#2563EB] font-medium"
                    : "text-[#4B5563] hover:bg-[#F9FAFB] hover:text-[#111827]"
                }`}
              >
                {item.icon}
                {item.name}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, hasCompany, logout, loading, isCA, isClient } = useAuth();
  const { plan, isTrial, trialDaysLeft, isPaid, isFree } = usePlan();

  const isActive = (path) => location.pathname === path;
  const currentLang = getLangFromPath(location.pathname);
  const isLocalisedMarketing = currentLang !== "en";
  const { t } = useT();

  // Hide global header on public SEO landing/sample pages — those render their own minimal header.
  const PUBLIC_SEO_PATHS = [
    "/gst-invoice-generator", "/free-invoice-generator", "/quotation-maker",
    "/tax-invoice-format", "/bill-format-small-business", "/proforma-invoice-generator",
    "/freelancer-invoice-tool", "/invoice-software-traders", "/billing-app-shop-owners",
    "/non-gst-bill-generator", "/for-ca", "/blog",
  ];
  if (
    PUBLIC_SEO_PATHS.includes(location.pathname) ||
    location.pathname.startsWith("/sample/") ||
    location.pathname.startsWith("/blog/") ||
    location.pathname.startsWith("/invoice-format/") ||
    /^\/(hi|mr|gu|ta)\/(for-ca|blog)$/.test(location.pathname)
  ) {
    return null;
  }

  const salesItems = isCA ? [] : [
    { name: "Quotations", path: "/quotations", icon: <ScrollText className="w-4 h-4" />, active: isActive("/quotations") || location.pathname.startsWith("/quotations/") },
    { name: "Create Invoice", path: "/create", icon: <Plus className="w-4 h-4" />, active: isActive("/create") },
    { name: "Upload & Analyze", path: "/upload", icon: <Upload className="w-4 h-4" />, active: isActive("/upload") },
    { name: "All Invoices", path: "/invoices", icon: <List className="w-4 h-4" />, active: isActive("/invoices") },
    { name: "Received Invoices", path: "/received", icon: <Inbox className="w-4 h-4" />, active: isActive("/received") },
    { name: "Recurring", path: "/recurring", icon: <Clock className="w-4 h-4" />, active: isActive("/recurring") },
  ];

  const invoiceItems = salesItems;  // alias kept for backwards compatibility

  const masterItems = isCA ? [] : [
    { name: "Products & Services", path: "/products", icon: <Package className="w-4 h-4" />, active: isActive("/products") },
    { name: "Customers", path: "/customers", icon: <Users className="w-4 h-4" />, active: isActive("/customers") },
  ];

  const financeItems = isCA ? [] : [
    { name: "GST Reports", path: "/reports", icon: <FileSpreadsheet className="w-4 h-4" />, active: isActive("/reports") },
    { name: "Financial Reports", path: "/financial-reports", icon: <TrendingUp className="w-4 h-4" />, active: isActive("/financial-reports") },
    { name: "Receivables", path: "/receivables", icon: <Wallet className="w-4 h-4" />, active: isActive("/receivables") },
    { name: "MSME Compliance", path: "/msme", icon: <Shield className="w-4 h-4" />, active: isActive("/msme") },
    { name: "Analytics", path: "/analytics", icon: <BarChart3 className="w-4 h-4" />, active: isActive("/analytics") },
  ];

  const dashboardPath = isCA ? "/ca/dashboard" : "/dashboard";

  const mobileGroups = isCA ? [] : [
    { label: "Sales", items: salesItems },
    { label: "Masters", items: masterItems },
    { label: "Reports & Finance", items: financeItems },
  ];

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to={isAuthenticated && (hasCompany || isCA) ? dashboardPath : "/"} className="flex items-center gap-2 group" data-testid="logo-link">
            <img src="/assets/logo.png" alt="InvoiceSetu" className="w-9 h-9 rounded-xl object-contain" />
            <span className="text-lg font-bold text-[#111827] hidden sm:block">
              Invoice<span className="gradient-text">Setu</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <Link
              to={isAuthenticated && (hasCompany || isCA) ? dashboardPath : prefixPath("/", currentLang)}
              data-testid="nav-link-home"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive("/") || isActive("/dashboard") || isActive("/ca/dashboard") || isActive(`/${currentLang}`) ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563] hover:text-[#111827] hover:bg-[#F3F4F6]"
              }`}
            >
              {isAuthenticated && (hasCompany || isCA) ? "Dashboard" : t("nav.home")}
            </Link>

            {isAuthenticated && (hasCompany || isCA) && !isCA && (
              <>
                <NavDropdown label="Sales" items={salesItems} />
                <NavDropdown label="Masters" items={masterItems} />
                <NavDropdown label="Finance" items={financeItems} />
              </>
            )}

            {!isCA && (
              <Link
                to="/find-ca"
                data-testid="nav-link-find-ca"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive("/find-ca") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563] hover:text-[#111827] hover:bg-[#F3F4F6]"
                }`}
              >
                Find CA
              </Link>
            )}

            {!isAuthenticated && (
              <Link
                to={prefixPath("/for-ca", currentLang)}
                data-testid="nav-link-for-ca"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname.endsWith("/for-ca") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563] hover:text-[#111827] hover:bg-[#F3F4F6]"
                }`}
              >
                {t("nav.forCA")}
              </Link>
            )}

            <Link
              to={prefixPath("/blog", currentLang)}
              data-testid="nav-link-blog"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                location.pathname.endsWith("/blog") || location.pathname.startsWith("/blog/") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563] hover:text-[#111827] hover:bg-[#F3F4F6]"
              }`}
            >
              {t("nav.blog")}
            </Link>

            {!isCA && (
              <Link
                to={prefixPath("/pricing", currentLang)}
                data-testid="nav-link-pricing"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname.endsWith("/pricing") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563] hover:text-[#111827] hover:bg-[#F3F4F6]"
                }`}
              >
                {t("nav.pricing")}
              </Link>
            )}
          </nav>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-2">
            {!isAuthenticated && (
              <LanguageSwitcher />
            )}
            {!loading && (
              <>
                {isAuthenticated && user ? (
                  <>
                    {isTrial && trialDaysLeft != null && (
                      <Link
                        to="/pricing"
                        className="hidden md:inline-flex items-center gap-1.5 bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 text-violet-700 text-xs font-semibold px-2.5 py-1 rounded-full hover:from-violet-100 hover:to-blue-100 transition-colors"
                        data-testid="trial-countdown-pill"
                        title="Growth Trial — click to manage"
                      >
                        <Clock className="w-3 h-3" /> Growth Trial: {trialDaysLeft} {trialDaysLeft === 1 ? "day" : "days"} left
                      </Link>
                    )}
                    <NotificationBell />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl hover:bg-[#F3F4F6] transition-colors" data-testid="user-menu-btn">
                          {user.picture ? (
                            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium">
                              {user.name?.charAt(0) || 'U'}
                            </div>
                          )}
                          <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <div className="px-2 py-1.5">
                          <p className="text-sm font-medium text-[#111827]">{user.name}</p>
                          <p className="text-xs text-[#6B7280] truncate">{user.email}</p>
                          <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isCA ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                            {isCA ? 'CA / Professional' : 'Business Owner'}
                          </span>
                        </div>
                        <DropdownMenuSeparator />
                        {/* Subscription Status */}
                        <div className="px-2 py-2" data-testid="header-plan-info">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Plan</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              isPaid ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                              isTrial ? 'bg-violet-50 text-violet-700 border border-violet-200' :
                              'bg-slate-100 text-slate-600 border border-slate-200'
                            }`} data-testid="header-plan-badge">
                              {isPaid ? (plan === 'yearly' ? 'Yearly' : 'Growth') : isTrial ? 'Trial' : 'Free'}
                            </span>
                          </div>
                          {isTrial && (
                            <p className="text-[11px] text-violet-600 mt-1" data-testid="header-trial-days">
                              {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left in trial
                            </p>
                          )}
                          {isPaid && (
                            <p className="text-[11px] text-emerald-600 mt-1" data-testid="header-paid-label">
                              {plan === 'yearly' ? 'Yearly' : 'Monthly'} subscription active
                            </p>
                          )}
                          {isFree && !isTrial && (
                            <Link to="/pricing" className="block text-[11px] text-violet-600 font-medium mt-1 hover:underline" data-testid="header-upgrade-link">
                              Upgrade for full access
                            </Link>
                          )}
                        </div>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                            <Settings className="w-4 h-4" /> Settings
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-[#EF4444] cursor-pointer" data-testid="logout-btn">
                          <LogOut className="w-4 h-4" /> Sign out
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <>
                    <Link
                      to="/auth"
                      className="text-sm font-medium text-[#4B5563] hover:text-[#111827] px-3 py-2 rounded-lg hover:bg-[#F3F4F6] transition-colors"
                      data-testid="login-btn"
                    >
                      {t("nav.signIn")}
                    </Link>
                    <Link
                      to="/auth"
                      className="btn-primary text-sm"
                      data-testid="header-get-started-btn"
                    >
                      {t("nav.signUp")}
                    </Link>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="lg:hidden p-2 rounded-xl hover:bg-[#F3F4F6] transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-[#111827]" /> : <Menu className="w-6 h-6 text-[#111827]" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-[#F3F4F6] bg-white"
            data-testid="mobile-menu"
          >
            <nav className="px-4 py-3 space-y-1">
              <Link
                to={isAuthenticated && (hasCompany || isCA) ? dashboardPath : "/"}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive("/") || isActive("/dashboard") || isActive("/ca/dashboard") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563]"
                }`}
              >
                {isAuthenticated && (hasCompany || isCA) ? "Dashboard" : "Home"}
              </Link>

              {isAuthenticated && hasCompany && mobileGroups.map((group) => (
                <div key={group.label}>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === group.label ? null : group.label)}
                    className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-[#4B5563]"
                    data-testid={`mobile-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {group.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === group.label ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {mobileExpanded === group.label && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="pl-4 space-y-0.5"
                      >
                        {group.items.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${
                              item.active ? "bg-[#EFF6FF] text-[#2563EB] font-medium" : "text-[#4B5563]"
                            }`}
                          >
                            {item.icon}
                            {item.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {!isCA && (
                <Link
                  to="/find-ca"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive("/find-ca") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563]"
                  }`}
                  data-testid="mobile-nav-find-ca"
                >
                  Find CA
                </Link>
              )}

              {!isAuthenticated && (
                <Link
                  to="/for-ca"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive("/for-ca") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563]"
                  }`}
                  data-testid="mobile-nav-for-ca"
                >
                  For CAs
                </Link>
              )}

              <Link
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive("/blog") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563]"
                }`}
                data-testid="mobile-nav-blog"
              >
                Blog
              </Link>

              {!isCA && (
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2.5 rounded-lg text-sm font-medium ${
                    isActive("/pricing") ? "bg-[#F3F4F6] text-[#111827]" : "text-[#4B5563]"
                  }`}
                >
                  Pricing
                </Link>
              )}

              {!loading && (
                <div className="pt-3 border-t border-[#F3F4F6]">
                  {isAuthenticated && user ? (
                    <>
                      <div className="px-3 py-2.5 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-medium">
                          {user.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#111827]">{user.name}</p>
                          <p className="text-xs text-[#6B7280]">{user.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/settings"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#4B5563] rounded-lg"
                      >
                        <Settings className="w-4 h-4" /> Settings
                      </Link>
                      <button
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-3 py-2.5 text-left text-sm font-medium text-[#EF4444] rounded-lg hover:bg-[#FEE2E2]"
                      >
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="px-3 py-2.5">
                        <LanguageSwitcher compact />
                      </div>
                      <Link
                        to="/auth"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block btn-primary text-sm text-center"
                      >
                        Sign in / Sign up
                      </Link>
                    </>
                  )}
                </div>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
